import type { CostComponentKind, Incoterm, Market, ShippingMode } from '@ambernord/shared';

/**
 * Offer/RFQ tipi — spogulis no `apps/api/src/routes/offers.ts` + `services/offers.ts`.
 * §4.9: klients saņem PROJICĒTU piedāvājumu (bez margin/supplier laukiem) + `costSummary`
 * kategorijas; admins saņem pilno rindu + `costComponents`. UI renderē tikai to, ko saņem.
 */

export interface ProductItem {
  id: string;
  sku: string;
  namesI18n: Record<string, string> | null;
}

/** Piedāvājuma rinda no GET /api/offers (adminam pilna; klientam bez redakcijas laukiem). */
export interface OfferRow {
  id: string;
  offerNo: string;
  customerId: string;
  market: Market;
  status: string;
  currency?: string;
  subtotal?: string | null;
  total?: string | null;
  /** Tikai admin projekcijā (§4.9). */
  marginPct?: string | null;
  /** Tikai admin projekcijā (§4.9). */
  supplierTenantId?: string | null;
  /** Piegādātāja legalName — tikai admin projekcijā (§4.9); null līdz komponēšanai. */
  supplierName?: string | null;
  carrierTenantId?: string | null;
  shippingMode?: ShippingMode | null;
  incoterm?: Incoterm | null;
  validUntil?: string | null;
  sentAt?: string | null;
  approvedAt?: string | null;
  placedOrderId?: string | null;
  notes?: string | null;
  createdAt?: string;
}

export interface OfferLineRow {
  id: string;
  productId: string;
  qty: string;
  unit: string;
}

/** Izmaksu komponente no DB (admin skats — GET /api/offers/:id). */
export interface CostComponentRow {
  kind: CostComponentKind;
  amount: string;
  label: string | null;
  pct?: string | null;
  rationale?: string | null;
}

/** Klienta GET /api/offers/:id atbilde (kategoriju kopsavilkums + LIELĀ gala cena). */
export interface CustomerOfferDetail {
  offer: OfferRow;
  costSummary: Array<{ label: string; amount: number }>;
  total: string | null;
  currency: string;
  lines: OfferLineRow[];
}

/** Admina GET /api/offers/:id atbilde (pilns sadalījums + iemesli). */
export interface AdminOfferDetail {
  offer: OfferRow;
  costComponents: CostComponentRow[];
  lines: OfferLineRow[];
}

/** Viena komponente `breakdown` atbildē no POST /:id/draft un /:id/combinations. */
export interface BreakdownComponent {
  kind: CostComponentKind;
  label?: string;
  amount: number;
  pct?: number;
  rationale?: string;
}

export interface CostBreakdown {
  components: BreakdownComponent[];
  subtotal: number;
  total: number;
  currency: string;
  marginPct?: number;
}

/** GET /api/offers/:id/combinations rinda (cheapest-first). */
export interface Combination {
  supplierTenantId: string;
  supplierName: string;
  carrierTenantId?: string;
  shippingMode: ShippingMode;
  incoterm: Incoterm;
  total: number;
  currency: string;
  breakdown: CostBreakdown;
}

/** API kļūdu kodi, kam ir tulkojums offers namespace `errors.*`. */
const KNOWN_ERRORS = new Set([
  'invalid_input',
  'not_found',
  'invalid_transition',
  'order_has_no_lines',
  'invalid_ship_to',
  'forbidden',
]);

/** Kļūdas koda → i18n atslēgas (offers ns) attēlojums ar generic atkāpšanos. */
export function errorKey(code: string | undefined | null): string {
  return code && KNOWN_ERRORS.has(code) ? `errors.${code}` : 'errors.generic';
}

/** Ship-to adrese no GET /api/customers/me (klienta pašam profilam). */
export interface ShipToAddress {
  id: string;
  label: string | null;
  line1: string;
  city: string;
  postalCode: string;
  country: string;
}

/** GET /api/customers/me atbilde — klienta pats profils + piegādes adreses. */
export interface CustomerProfile {
  customer: { id: string; legalName: string; market: Market };
  shipTo: ShipToAddress[];
}

/** Īss tenant ID marķieris, ja vārds nav pieejams (deterministisks fragments). */
export function shortId(id: string | null | undefined): string {
  return id ? `#${id.slice(0, 8)}` : '—';
}

/** Piegādātāja etiķete admin skatam: legalName no API, citādi ID fragments. */
export function supplierLabel(row: {
  supplierName?: string | null;
  supplierTenantId?: string | null;
}): string {
  return row.supplierName ?? shortId(row.supplierTenantId);
}

/** Ship-to opcijas teksts: etiķete (vai 1. rinda) + pilsēta + valsts. */
export function shipToLabel(a: ShipToAddress): string {
  return `${a.label ?? a.line1} · ${a.city}, ${a.country}`;
}

/** Lomas, kas drīkst komponēt/nosūtīt piedāvājumus (`offer:update`). */
export function canManageOffers(role: string): boolean {
  return role === 'platform_super' || role === 'platform_admin' || role === 'platform_ops';
}

/** Statusu secība admin ciļņu joslai. */
export const OFFER_TABS = [
  'quote_requested',
  'offer_drafted',
  'offer_sent',
  'offer_approved',
] as const;
export type OfferTab = (typeof OFFER_TABS)[number];

/** Jaunākie vispirms (createdAt dilstoši, bez datuma — beigās). */
export function byNewest(a: OfferRow, b: OfferRow): number {
  return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
}
